<script lang="ts" setup>
import { onUnmounted, watch } from 'vue';
import { usePageHeading, usePageHeadingOwner } from '../composables/usePageHeading';

const props = defineProps<{
  breadcrumbs?: { label: string; to?: string }[];
  /**
   * App-bar page title. Prefer the page's original useHead title
   * (e.g. "レベニューセンター一覧"). Falls back to the last breadcrumb label.
   */
  title?: string;
  /** Docs path for contextual Help in the app bar (next to the page title). */
  helpPath?: string;
}>();

const { setPageHeading, clearPageHeading } = usePageHeading();
/** Stable id for this PageContent instance — used to avoid clearing a newer page's heading. */
const owner = usePageHeadingOwner();

const publish = () => {
  const items = props.breadcrumbs ?? [];
  const title = (props.title || '').trim() || items[items.length - 1]?.label || '';
  if (!title) {
    clearPageHeading(owner);
    return;
  }

  // Explicit title: use first crumb as light parent on new/edit pages.
  // Breadcrumb-only: keep previous crumb as parent.
  const parent = props.title
    ? items.length > 1
      ? items[0].label
      : undefined
    : items.length > 1
      ? items[items.length - 2].label
      : undefined;

  setPageHeading(
    {
      title,
      parent,
      helpPath: props.helpPath,
    },
    owner,
  );
};

watch(() => [props.breadcrumbs, props.title, props.helpPath] as const, publish, {
  immediate: true,
  deep: true,
});

onUnmounted(() => clearPageHeading(owner));
</script>

<template>
  <div class="page-content">
    <!-- Non-help actions only (Compose, Call support, etc.). Help uses helpPath. -->
    <div
      v-if="$slots.actions"
      class="page-content__actions"
    >
      <slot name="actions" />
    </div>

    <div class="page-content__body">
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.page-content {
  flex: 1;
  display: flex;
  flex-direction: column;

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
    padding: 16px 20px 0;
  }

  &__body {
    padding: 20px 20px 24px;
    flex: 1;
    display: flex;
    flex-direction: column;

    > div,
    > form,
    > .v-form {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  }
}
</style>
